import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { catchError, finalize, forkJoin, map, of, switchMap, take } from 'rxjs';
import {
  MainProductCategory,
  CategoryProfitRule,
  CategoryProfitRuleMutation,
  ProductBrand,
  ProductBrandMutation,
  ProductCategory,
  ProductCategoryMutation,
} from '../../core/models/product.models';
import { ConfirmationService } from '../../core/services/confirmation.service';
import { ProductsService } from '../../core/services/products.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { CurrentUserService } from '../../core/auth/current-user.service';
import { leafProductCategories } from '../../core/utils/product-category-tree';

type CatalogTab = 'categories' | 'brands' | 'profit';

@Component({
  selector: 'app-catalog',
  imports: [FormsModule],
  templateUrl: './catalog.html',
  styleUrl: './catalog.scss',
})
export class Catalog implements OnInit {
  private readonly productsService = inject(ProductsService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly currentUser = inject(CurrentUserService);
  private requestedProfitCategoryId = '';
  readonly profitOnly = Boolean(this.route.snapshot.data['profitOnly']);
  can(permission: string): boolean { return this.currentUser.hasPermission(permission); }

  readonly mainCategories: MainProductCategory[] = ['Electronics', 'Vehicle'];
  activeTab: CatalogTab = 'categories';
  parentCategories: ProductCategory[] = [];
  childCategories: ProductCategory[] = [];
  brands: ProductBrand[] = [];
  profitRules: CategoryProfitRule[] = [];
  profitCategoryId = '';
  editingProfitRuleId = '';
  profitForm: CategoryProfitRuleMutation = { minimumProductValue: 0, profitPercentage: 0, isActive: true };
  isLoading = false;
  isProfitLoading = false;
  isSaving = false;
  errorMessage = '';
  successMessage = '';
  editingCategoryId = '';
  editingBrandId = '';
  categoryParentCategoryId = '';
  categoryParentSubCategoryId = '';
  brandCategoryId = '';
  brandSubCategoryId = '';
  brandSubSubCategoryId = '';

  categoryForm = this.emptyCategoryForm();
  brandForm = this.emptyBrandForm();

  ngOnInit(): void {
    const query = this.route.snapshot.queryParamMap;
    if (this.profitOnly || query.get('tab') === 'profit') this.activeTab = 'profit';
    this.requestedProfitCategoryId = query.get('categoryId') ?? '';
    const minimumProductValue = Number(query.get('minimumProductValue'));
    if (Number.isFinite(minimumProductValue) && minimumProductValue >= 0) {
      this.profitForm.minimumProductValue = minimumProductValue;
    }
    this.loadCatalog();
  }

  get allCategories(): ProductCategory[] {
    return [...this.parentCategories, ...this.childCategories];
  }

  get profitCategories(): ProductCategory[] {
    return leafProductCategories(this.allCategories)
      .sort((left, right) => this.categoryDisplayName(left).localeCompare(this.categoryDisplayName(right)));
  }

  get categoryRootOptions(): ProductCategory[] {
    return this.parentCategories.filter((category) => category.id !== this.editingCategoryId);
  }

  get categorySubOptions(): ProductCategory[] {
    return this.childrenOf(this.categoryParentCategoryId).filter((category) => category.id !== this.editingCategoryId);
  }

  get brandRootOptions(): ProductCategory[] {
    return this.parentCategories;
  }

  get brandSubOptions(): ProductCategory[] {
    return this.childrenOf(this.brandCategoryId);
  }

  get brandSubSubOptions(): ProductCategory[] {
    return this.childrenOf(this.brandSubCategoryId);
  }

  setTab(tab: CatalogTab): void {
    this.activeTab = tab;
    this.errorMessage = '';
    this.successMessage = '';
    if (tab === 'profit') this.loadProfitRules();
  }

  loadCatalog(): void {
    this.isLoading = true;
    this.errorMessage = '';

    forkJoin({ categories: this.productsService.categories(), brands: this.productsService.brands() })
      .pipe(
        switchMap(({ categories, brands }) => {
          this.parentCategories = categories.filter((category): category is ProductCategory => 'id' in category);
          this.brands = brands;
          if (!this.parentCategories.length) {
            return of([] as ProductCategory[]);
          }
          return forkJoin(this.parentCategories.map((category) => this.productsService.childCategories(category.id))).pipe(
            map((groups) => groups.flat()),
            switchMap((subcategories) => {
              if (!subcategories.length) return of(subcategories);
              return forkJoin(subcategories.map((category) => this.productsService.childCategories(category.id))).pipe(
                map((groups) => [...subcategories, ...groups.flat()]),
              );
            }),
          );
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (children) => {
          this.childCategories = children;
          this.isLoading = false;
          if (!this.profitCategoryId) {
            this.profitCategoryId = this.profitCategories.some((category) => category.id === this.requestedProfitCategoryId)
              ? this.requestedProfitCategoryId
              : this.profitCategories[0]?.id ?? '';
          }
          if (this.activeTab === 'profit') this.loadProfitRules();
        },
        error: (error: unknown) => {
          this.isLoading = false;
          this.errorMessage = httpErrorMessage(error, 'Unable to load catalog data.');
        },
      });
  }

  saveCategory(): void {
    const parentCategoryId = this.categoryParentSubCategoryId || this.categoryParentCategoryId;
    const payload: ProductCategoryMutation = {
      parentCategoryId: parentCategoryId || undefined,
      name: this.categoryForm.name.trim(),
      slug: this.categoryForm.slug.trim() || this.slugify(this.categoryForm.name),
      mainCategory: this.categoryForm.mainCategory,
      isActive: true,
      icon: this.categoryForm.icon,
    };

    if (!payload.name) {
      this.errorMessage = 'Category name is required.';
      return;
    }

    this.isSaving = true;
    this.clearMessages();
    const request = this.editingCategoryId
      ? this.productsService.updateCategory(this.editingCategoryId, payload)
      : this.productsService.createCategory(payload);
    request.pipe(finalize(() => (this.isSaving = false))).subscribe({
      next: () => {
        this.successMessage = this.editingCategoryId ? 'Category updated.' : 'Category created.';
        this.cancelCategoryEdit();
        this.loadCatalog();
      },
      error: (error: unknown) => {
        this.errorMessage = httpErrorMessage(error, 'Unable to save category.');
      },
    });
  }

  editCategory(category: ProductCategory): void {
    this.editingCategoryId = category.id;
    this.categoryForm = {
      parentCategoryId: category.parentCategoryId ?? '',
      name: category.name,
      slug: category.slug ?? '',
      mainCategory: this.mainCategoryName(category.mainCategory),
      icon: undefined,
    };
    const parentPath = this.categoryPath(category.parentCategoryId);
    this.categoryParentCategoryId = parentPath[0]?.id ?? '';
    this.categoryParentSubCategoryId = parentPath[1]?.id ?? '';
    this.activeTab = 'categories';
  }

  cancelCategoryEdit(): void {
    this.editingCategoryId = '';
    this.categoryForm = this.emptyCategoryForm();
    this.categoryParentCategoryId = '';
    this.categoryParentSubCategoryId = '';
  }

  async deactivateCategory(category: ProductCategory): Promise<void> {
    const confirmed = await this.confirmation.confirmDestructive(
      'Deactivate category?',
      `${category.name} will no longer be available for new product listings.`,
      'Deactivate',
    );
    if (!confirmed) return;

    this.isSaving = true;
    this.clearMessages();
    this.productsService
      .deactivateCategory(category.id)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          this.successMessage = 'Category deactivated.';
          this.loadCatalog();
        },
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to deactivate category.');
        },
      });
  }

  saveBrand(): void {
    const categoryId = this.brandSubSubCategoryId || this.brandSubCategoryId || this.brandCategoryId;
    const selectedCategory = this.allCategories.find((category) => category.id === categoryId);
    const payload: ProductBrandMutation = {
      categoryId: categoryId || undefined,
      name: this.brandForm.name.trim(),
      slug: this.brandForm.slug.trim() || this.slugify(this.brandForm.name),
      mainCategory: selectedCategory ? this.mainCategoryName(selectedCategory.mainCategory) : this.brandForm.mainCategory,
      isActive: true,
      icon: this.brandForm.icon,
    };

    if (!payload.name) {
      this.errorMessage = 'Brand name is required.';
      return;
    }
    if (!payload.categoryId) {
      this.errorMessage = 'Select a category for the brand.';
      return;
    }

    this.isSaving = true;
    this.clearMessages();
    const request = this.editingBrandId
      ? this.productsService.updateBrand(this.editingBrandId, payload)
      : this.productsService.createBrand(payload);
    request.pipe(finalize(() => (this.isSaving = false))).subscribe({
      next: () => {
        this.successMessage = this.editingBrandId ? 'Brand updated.' : 'Brand created.';
        this.cancelBrandEdit();
        this.loadCatalog();
      },
      error: (error: unknown) => {
        this.errorMessage = httpErrorMessage(error, 'Unable to save brand.');
      },
    });
  }

  editBrand(brand: ProductBrand): void {
    this.editingBrandId = brand.id;
    this.brandForm = {
      categoryId: brand.categoryId ?? '',
      name: brand.name,
      slug: brand.slug ?? '',
      mainCategory: this.mainCategoryName(brand.mainCategory),
      icon: undefined,
    };
    const categoryPath = this.categoryPath(brand.categoryId);
    this.brandCategoryId = categoryPath[0]?.id ?? '';
    this.brandSubCategoryId = categoryPath[1]?.id ?? '';
    this.brandSubSubCategoryId = categoryPath[2]?.id ?? '';
    this.activeTab = 'brands';
  }

  cancelBrandEdit(): void {
    this.editingBrandId = '';
    this.brandForm = this.emptyBrandForm();
    this.brandCategoryId = '';
    this.brandSubCategoryId = '';
    this.brandSubSubCategoryId = '';
  }

  onCategoryParentChange(): void {
    this.categoryParentSubCategoryId = '';
    const root = this.parentCategories.find((category) => category.id === this.categoryParentCategoryId);
    if (root) this.categoryForm.mainCategory = this.mainCategoryName(root.mainCategory);
  }

  onBrandMainChange(): void {
    this.brandCategoryId = '';
    this.brandSubCategoryId = '';
    this.brandSubSubCategoryId = '';
    this.brandForm.categoryId = '';
  }

  onBrandCategoryChange(): void {
    this.brandSubCategoryId = '';
    this.brandSubSubCategoryId = '';
    const root = this.parentCategories.find((category) => category.id === this.brandCategoryId);
    if (root) this.brandForm.mainCategory = this.mainCategoryName(root.mainCategory);
  }

  onBrandSubCategoryChange(): void {
    this.brandSubSubCategoryId = '';
  }

  async deactivateBrand(brand: ProductBrand): Promise<void> {
    const confirmed = await this.confirmation.confirmDestructive(
      'Deactivate brand?',
      `${brand.name} will no longer be available for new product listings.`,
      'Deactivate',
    );
    if (!confirmed) return;

    this.isSaving = true;
    this.clearMessages();
    this.productsService
      .deactivateBrand(brand.id)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          this.successMessage = 'Brand deactivated.';
          this.loadCatalog();
        },
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to deactivate brand.');
        },
      });
  }

  categoryIconChanged(event: Event): void {
    this.categoryForm.icon = (event.target as HTMLInputElement).files?.[0];
  }

  brandIconChanged(event: Event): void {
    this.brandForm.icon = (event.target as HTMLInputElement).files?.[0];
  }

  categoryName(categoryId: string | null | undefined): string {
    return this.allCategories.find((category) => category.id === categoryId)?.name ?? 'All categories';
  }

  categoryDisplayName(category: ProductCategory): string {
    return this.categoryPath(category.id).map((item) => item.name).join(' / ');
  }

  profitCategoryName(categoryId: string): string {
    const category = this.allCategories.find((item) => item.id === categoryId);
    return category ? this.categoryDisplayName(category) : 'Unknown category';
  }

  parentName(category: ProductCategory): string {
    return category.parentCategoryId ? this.categoryName(category.parentCategoryId) : 'Main category';
  }

  onProfitCategoryChange(): void {
    this.cancelProfitEdit();
  }

  loadProfitRules(): void {
    const categories = this.profitCategories;
    if (!categories.length) {
      this.profitRules = [];
      return;
    }

    this.isProfitLoading = true;
    this.errorMessage = '';
    const categoryOrder = new Map(categories.map((category, index) => [category.id, index]));
    forkJoin(categories.map((category) => this.productsService.profitRules(category.id).pipe(
      map((rules) => ({ rules, error: null as unknown })),
      catchError((error: unknown) => of({ rules: [] as CategoryProfitRule[], error })),
    )))
      .pipe(finalize(() => (this.isProfitLoading = false)))
      .subscribe({
        next: (groups) => {
          this.isProfitLoading = false;
          this.profitRules = groups.flatMap((group) => group.rules).sort((left, right) =>
            (categoryOrder.get(left.categoryId) ?? 0) - (categoryOrder.get(right.categoryId) ?? 0)
            || left.minimumProductValue - right.minimumProductValue,
          );
          const failures = groups.filter((group) => group.error !== null);
          if (failures.length) {
            const message = httpErrorMessage(failures[0].error, 'Unable to load category profit rules.');
            this.errorMessage = failures.length === groups.length
              ? message
              : `Some category profit rules could not be loaded. ${message}`;
          }
          this.cdr.detectChanges();
        },
      });
  }

  saveProfitRule(): void {
    if (this.isSaving) return;

    if (!this.profitCategoryId) {
      this.errorMessage = 'Select a category.';
      return;
    }
    if (this.profitForm.minimumProductValue < 0 || this.profitForm.profitPercentage < 0 || this.profitForm.profitPercentage > 100) {
      this.errorMessage = 'Enter a valid minimum value and profit percentage.';
      return;
    }

    this.isSaving = true;
    this.clearMessages();
    const editingRuleId = this.editingProfitRuleId;
    const request = editingRuleId
      ? this.productsService.updateProfitRule(editingRuleId, this.profitForm)
      : this.productsService.createProfitRule(this.profitCategoryId, this.profitForm);
    request.pipe(take(1), finalize(() => (this.isSaving = false))).subscribe({
      next: (savedRule) => {
        this.isSaving = false;
        this.upsertProfitRule(savedRule);
        this.successMessage = editingRuleId ? 'Profit rule updated.' : 'Profit rule created.';
        this.cancelProfitEdit();
        this.cdr.detectChanges();
        this.loadProfitRules();
      },
      error: (error: unknown) => {
        this.isSaving = false;
        this.errorMessage = httpErrorMessage(error, 'Unable to save profit rule.');
        this.cdr.detectChanges();
      },
    });
  }

  private upsertProfitRule(savedRule: CategoryProfitRule): void {
    const existingIndex = this.profitRules.findIndex((rule) => rule.id === savedRule.id);
    this.profitRules = existingIndex < 0
      ? [...this.profitRules, savedRule]
      : this.profitRules.map((rule) => rule.id === savedRule.id ? savedRule : rule);
  }

  editProfitRule(rule: CategoryProfitRule): void {
    this.editingProfitRuleId = rule.id;
    this.profitForm = {
      minimumProductValue: rule.minimumProductValue,
      profitPercentage: rule.profitPercentage,
      isActive: rule.isActive,
    };
  }

  cancelProfitEdit(): void {
    this.editingProfitRuleId = '';
    this.profitForm = { minimumProductValue: 0, profitPercentage: 0, isActive: true };
  }

  async deactivateProfitRule(rule: CategoryProfitRule): Promise<void> {
    const confirmed = await this.confirmation.confirmDestructive(
      'Deactivate profit rule?',
      `The ${rule.profitPercentage}% rule starting at ${this.formatAmount(rule.minimumProductValue)} will stop being used.`,
      'Deactivate',
    );
    if (!confirmed) return;

    this.isSaving = true;
    this.clearMessages();
    this.productsService.deactivateProfitRule(rule.id).pipe(finalize(() => (this.isSaving = false))).subscribe({
      next: () => {
        this.successMessage = 'Profit rule deactivated.';
        this.profitRules = this.profitRules.map((item) => item.id === rule.id ? { ...item, isActive: false } : item);
        this.cdr.detectChanges();
        this.loadProfitRules();
      },
      error: (error: unknown) => {
        this.errorMessage = httpErrorMessage(error, 'Unable to deactivate profit rule.');
        this.cdr.detectChanges();
      },
    });
  }

  formatAmount(value: number): string {
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(value);
  }

  private emptyCategoryForm() {
    return {
      parentCategoryId: '',
      name: '',
      slug: '',
      mainCategory: 'Electronics' as MainProductCategory,
      icon: undefined as File | undefined,
    };
  }

  private emptyBrandForm() {
    return {
      categoryId: '',
      name: '',
      slug: '',
      mainCategory: 'Electronics' as MainProductCategory,
      icon: undefined as File | undefined,
    };
  }

  mainCategoryName(value: ProductCategory['mainCategory'] | ProductBrand['mainCategory']): MainProductCategory {
    return value === 2 || value === 'Vehicle' ? 'Vehicle' : 'Electronics';
  }

  private slugify(value: string): string {
    return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  private childrenOf(parentCategoryId: string): ProductCategory[] {
    return parentCategoryId ? this.childCategories.filter((category) => category.parentCategoryId === parentCategoryId) : [];
  }

  private categoryPath(categoryId: string | null | undefined): ProductCategory[] {
    const path: ProductCategory[] = [];
    let current = this.allCategories.find((category) => category.id === categoryId);
    while (current) {
      path.unshift(current);
      current = this.allCategories.find((category) => category.id === current?.parentCategoryId);
    }
    return path;
  }
}
